import type { DeckId } from '../../modules/decks/generate';
import type { SourceDeck } from '../../modules/decks/sourceDeck';
import { introDeck } from './introDeck';
import { workshopDeck } from './workshopDeck';

/**
 * Where each step's deck comes from.
 *
 * A deck is a managed `CampaignAsset` (`kind='deck'`): the content team uploads the .pptx in Admin, the
 * queue worker converts it to a PDF for the on-screen preview, and the download hands over the uploaded
 * file via a short-lived signed GET. `resolveDeckSources()` rebuilds this map from those assets'
 * `metadata` at runtime, so there is no bundled deck file shipped in the build.
 *
 * This base map is the generic no-asset fallback only — the text-only render used when a campaign has
 * uploaded no deck asset at all. A deck is described two ways and the app uses whichever it has:
 *
 * - `deck`: the slide text (titles, bullets), rendered directly when no slide images are available.
 * - `images`/`file`: optional per-slide image directory / downloadable file. Populated only from an
 *   asset's `metadata` (never a bundled `public/` path); absent on this base map.
 */
export interface DeckSourceConfig {
  deck: SourceDeck;
  /** Small label above the title on the text fallback. */
  kicker: string;
  /**
   * Directory of rendered slide images (one `slide-N.jpg` per slide), from an asset's `metadata`. The
   * text fallback renders without it. Never a bundled `public/` path.
   */
  images?: string;
  /** The file offered for download, from an asset's `metadata`. Never a bundled `public/` path. */
  file?: string;
  /**
   * When this deck is backed by an uploaded `CampaignAsset`, its row id — so the download can mint a
   * short-lived signed GET for the uploaded bytes instead of the bundled public `file`. Absent for
   * bundled decks.
   */
  assetId?: number | string;
  /** The uploaded asset's S3 object key (empty until a file is uploaded). Absent for bundled decks. */
  objectKey?: string;
}

export const DECK_SOURCES: Record<DeckId, DeckSourceConfig> = {
  pitch: {
    deck: introDeck,
    kicker: 'Cisco Frontier AI',
  },
  workshop: {
    deck: workshopDeck,
    kicker: 'Cisco Frontier AI Workshop',
  },
};
